import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  IconButton,
  LinearProgress,
  Modal,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import IosShareRoundedIcon from "@mui/icons-material/IosShareRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import GroupAddRoundedIcon from "@mui/icons-material/GroupAddRounded";
import {
  dismissReferralPrompt,
  ensureReferralLink,
  recordReferralLinkCopied,
  recordReferralNativeShareStarted,
} from "../../api/referrals";
import { isDarkMode } from "../../utils/constants";

const promptCopy = (program = {}) => {
  const progress = `${program.qualifiedCount || 0}/${program.threshold || 5}`;
  const remaining = program.remainingCount || 0;
  const rewardDescription = program.rewardOfferDescription || program.rewardDisplayLabel || "Incite credit";
  const withReward = (copy) => ({ ...copy, rewardDescription });

  if (program.promptKind === "reward_earned" || program.rewardAvailable) {
    return withReward({
      eyebrow: "Referral reward",
      title: "Your referral reward is ready",
      body: "You hit 5 qualified friends. Your reward is ready for your subscription.",
      cta: "Copy invite link",
    });
  }

  if (program.promptKind === "finish_progress") {
    return withReward({
      eyebrow: `${progress} friends qualified or pending`,
      title: "You are close to your reward",
      body: `Share your invite link again. ${remaining} more qualified ${remaining === 1 ? "friend" : "friends"} unlocks your referral reward.`,
      cta: "Copy link",
    });
  }

  if (program.promptKind === "keep_sharing" || program.promptKind === "monthly_progress") {
    return withReward({
      eyebrow: `${progress} progress`,
      title: "Keep your referral reward moving",
      body: "Friends count after they confirm email and ask their first Incite question.",
      cta: "Copy link",
    });
  }

  if (program.promptKind === "post_satisfaction") {
    return withReward({
      eyebrow: "Invite friends",
      title: "Invite friends. Earn Incite credit.",
      body: "Invite 5 friends. Once they sign up, confirm email, and ask their first question, your reward unlocks.",
      cta: "Copy invite link",
    });
  }

  if (program.promptKind === "first_paid_invite") {
    return withReward({
      eyebrow: "Member referral",
      title: "Invite friends. Earn Incite credit.",
      body: "Share your personal link with people who would use real-time market intelligence.",
      cta: "Copy invite link",
    });
  }

  return withReward({
    eyebrow: "Member referral",
    title: "Invite friends. Earn Incite credit.",
    body: "Share your personal link with people who would use real-time market intelligence.",
    cta: "Copy invite link",
  });
};

const ReferralInviteModal = ({
  open,
  onClose,
  program,
  onProgramUpdate,
  surface = "account",
  dismissOnClose = false,
}) => {
  const [localProgram, setLocalProgram] = useState(program);
  const [copyState, setCopyState] = useState("idle");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setLocalProgram(program);
  }, [program]);

  const currentProgram = localProgram || {};
  const copy = promptCopy(currentProgram);
  const threshold = currentProgram.threshold || 5;
  const qualifiedCount = currentProgram.qualifiedCount || 0;
  const pendingCount = currentProgram.pendingCount || 0;
  const progressPct = Math.min((qualifiedCount / threshold) * 100, 100);

  const updateProgram = (nextProgram) => {
    if (nextProgram && !nextProgram.error) {
      setLocalProgram(nextProgram);
      if (onProgramUpdate) onProgramUpdate(nextProgram);
    }
    return nextProgram;
  };

  const ensureProgram = async () => {
    if (currentProgram.shareUrl) return currentProgram;
    const ensured = await ensureReferralLink(surface);
    return updateProgram(ensured) || ensured;
  };

  const copyLink = async () => {
    if (busy) return;
    setBusy(true);
    setCopyState("idle");
    try {
      const ensured = await ensureProgram();
      if (!ensured?.shareUrl) throw new Error("Missing referral link");
      await navigator.clipboard.writeText(ensured.shareUrl);
      const updated = await recordReferralLinkCopied(surface);
      updateProgram(updated);
      setCopyState("copied");
      window.setTimeout(() => setCopyState("idle"), 2200);
    } catch (error) {
      console.error("copy referral link failed", error);
      setCopyState("failed");
    } finally {
      setBusy(false);
    }
  };

  const nativeShare = async () => {
    if (busy) return;
    const ensured = await ensureProgram();
    if (!ensured?.shareUrl) return;

    if (!navigator.share) {
      copyLink();
      return;
    }

    setBusy(true);
    try {
      const updated = await recordReferralNativeShareStarted(surface);
      updateProgram(updated);
      await navigator.share({
        title: "Try Incite",
        text: "I've been using Incite for market intelligence. Try it here:",
        url: ensured.shareUrl,
      });
    } catch (error) {
      if (error?.name !== "AbortError") {
        console.error("native referral share failed", error);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleClose = async () => {
    if (dismissOnClose) {
      const updated = await dismissReferralPrompt(surface);
      updateProgram(updated);
    }
    if (onClose) onClose();
  };

  return (
    <Modal
      open={!!open}
      onClose={handleClose}
      aria-labelledby="referral-invite-title"
      sx={{ zIndex: 1700 }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(94vw, 520px)",
          maxHeight: "88vh",
          overflowY: "auto",
          borderRadius: "24px",
          outline: "none",
          color: "#FFFFFF",
          boxShadow: "0 24px 80px rgba(0,0,0,0.38)",
          background: isDarkMode
            ? "linear-gradient(155deg, #111827 0%, #172033 46%, #0F766E 150%)"
            : "linear-gradient(155deg, #0B1220 0%, #172B4D 52%, #0F766E 155%)",
          border: "1px solid rgba(255,255,255,0.14)",
        }}
      >
        <IconButton
          aria-label="Close referral invite"
          onClick={handleClose}
          size="small"
          sx={{
            position: "absolute",
            top: 14,
            right: 14,
            color: "rgba(255,255,255,0.86)",
            background: "rgba(255,255,255,0.08)",
            "&:hover": { background: "rgba(255,255,255,0.14)" },
          }}
        >
          <CloseRoundedIcon fontSize="small" />
        </IconButton>

        <Box sx={{ p: { xs: "28px 22px 24px", sm: "34px 34px 30px" } }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              px: "11px",
              py: "6px",
              borderRadius: "999px",
              background: "rgba(94, 214, 196, 0.14)",
              color: "#9EF3E2",
              fontSize: "12px",
              fontWeight: 700,
              letterSpacing: 0,
            }}
          >
            <GroupAddRoundedIcon sx={{ fontSize: 16 }} />
            {copy.eyebrow}
          </Box>

          <Typography
            id="referral-invite-title"
            sx={{
              mt: 2,
              maxWidth: "410px",
              fontSize: { xs: "28px", sm: "34px" },
              lineHeight: 1.05,
              fontWeight: 800,
              letterSpacing: 0,
            }}
          >
            {copy.title}
          </Typography>

          <Typography
            sx={{
              mt: 1.4,
              color: "rgba(255,255,255,0.76)",
              fontSize: "15px",
              lineHeight: 1.55,
              maxWidth: "430px",
            }}
          >
            {copy.body}
          </Typography>

          <Box
            sx={{
              mt: 2,
              width: "fit-content",
              maxWidth: "100%",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              px: "13px",
              py: "10px",
              borderRadius: "14px",
              background: "rgba(94, 214, 196, 0.14)",
              border: "1px solid rgba(158, 243, 226, 0.22)",
            }}
          >
            <CheckCircleRoundedIcon sx={{ color: "#5ED6C4", fontSize: 18, flexShrink: 0 }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ color: "rgba(255,255,255,0.62)", fontSize: "11px", fontWeight: 800, lineHeight: 1 }}>
                Reward
              </Typography>
              <Typography sx={{ mt: 0.35, color: "#FFFFFF", fontSize: "14px", fontWeight: 850, lineHeight: 1.25, overflowWrap: "anywhere" }}>
                {copy.rewardDescription}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mt: 3.2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography sx={{ fontSize: "13px", color: "rgba(255,255,255,0.72)", fontWeight: 600 }}>
                Qualified progress
              </Typography>
              <Typography sx={{ fontSize: "13px", color: "#FFFFFF", fontWeight: 800 }}>
                {qualifiedCount} of {threshold} friends
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progressPct}
              sx={{
                height: 9,
                borderRadius: "999px",
                backgroundColor: "rgba(255,255,255,0.12)",
                ".MuiLinearProgress-bar": {
                  borderRadius: "999px",
                  background: "linear-gradient(90deg, #5ED6C4 0%, #65A8FF 100%)",
                },
              }}
            />
            {pendingCount > 0 && (
              <Typography sx={{ mt: 1, fontSize: "12px", color: "rgba(255,255,255,0.64)" }}>
                {pendingCount} pending {pendingCount === 1 ? "friend" : "friends"} can still qualify.
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              mt: 3,
              display: "flex",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <Button
              onClick={copyLink}
              disabled={busy || !currentProgram.eligible}
              startIcon={copyState === "copied" ? <CheckCircleRoundedIcon /> : <ContentCopyRoundedIcon />}
              sx={{
                minHeight: "44px",
                px: "18px",
                borderRadius: "999px",
                textTransform: "none",
                fontWeight: 800,
                color: "#07111F",
                background: "#FFFFFF",
                "&:hover": { background: "rgba(255,255,255,0.88)" },
                "&.Mui-disabled": { color: "rgba(7,17,31,0.45)", background: "rgba(255,255,255,0.5)" },
              }}
            >
              {copyState === "copied" ? "Copied" : copy.cta}
            </Button>
            <Button
              onClick={nativeShare}
              disabled={busy || !currentProgram.eligible}
              startIcon={<IosShareRoundedIcon />}
              sx={{
                minHeight: "44px",
                px: "18px",
                borderRadius: "999px",
                textTransform: "none",
                fontWeight: 800,
                color: "#FFFFFF",
                border: "1px solid rgba(255,255,255,0.26)",
                background: "rgba(255,255,255,0.08)",
                "&:hover": { background: "rgba(255,255,255,0.14)" },
              }}
            >
              Share
            </Button>
          </Box>

          {copyState === "failed" && (
            <Typography sx={{ mt: 1.3, color: "#FCA5A5", fontSize: "12px" }}>
              Could not copy the link. Try Share or open this from Account.
            </Typography>
          )}

          <Accordion
            disableGutters
            sx={{
              mt: 3,
              borderTop: "1px solid rgba(255,255,255,0.12)",
              color: "#FFFFFF",
              background: "transparent",
              boxShadow: "none",
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreRoundedIcon sx={{ color: "rgba(255,255,255,0.72)" }} />}
              sx={{
                minHeight: "44px",
                px: 0,
                py: 0.4,
                "& .MuiAccordionSummary-content": { my: 0.8 },
              }}
            >
              <Typography sx={{ color: "rgba(255,255,255,0.82)", fontSize: "13px", fontWeight: 800 }}>
                How referrals qualify
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0, pt: 0, pb: 0.4, display: "grid", gap: "8px" }}>
              {(currentProgram.terms || []).map((term) => (
                <Box key={term} sx={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
                  <CheckCircleRoundedIcon sx={{ color: "#5ED6C4", fontSize: 16, mt: "2px" }} />
                  <Typography sx={{ color: "rgba(255,255,255,0.68)", fontSize: "12px", lineHeight: 1.45 }}>
                    {term}
                  </Typography>
                </Box>
              ))}
            </AccordionDetails>
          </Accordion>
        </Box>
      </Box>
    </Modal>
  );
};

export default ReferralInviteModal;
